import { useParams, Link, useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { Helmet } from "react-helmet-async";
import { supabase } from "@/integrations/supabase/client";
import { Loader2, ArrowLeft, User, ArrowRight } from "lucide-react";
import { Button } from "@/components/ui/button";
import PostViewer from "@/components/blog/PostViewer";
import ArticleTableOfContents from "@/components/blog/ArticleTableOfContents";
import FloatingArticleNav from "@/components/blog/FloatingArticleNav";
import { ArticleHeader, AuthorCard, DynamicCtaFooter, ShareBar, ArticleFreshness, RelatedArticlesSection, RelatedArticle, PeopleProfileDialog } from '@/components/ds';
import linkedinIconSquare from "@/assets/linkedin-icon-square.svg";
import { optimizeImage } from "@/lib/imageOptimization";
import { useDisclaimer } from '@/hooks/useDisclaimers';
import {
  Navbar,
  NavBody,
  NavItems,
  MobileNav,
  NavbarLogo,
  NavbarButton,
  MobileNavHeader,
  MobileNavToggle,
  MobileNavMenu,
} from "@/components/ui/resizable-navbar";
import { MobileMenuContent } from "@/components/MobileMenuContent";
import { useState, useMemo, useEffect, useCallback, useRef } from "react";
import logoWhite from "@/assets/logo-white.svg";
import logoBlack from "@/assets/logo-black.svg";
import Footer from "@/components/Footer";
import { useWaitlistModal } from "@/components/waitlist/WaitlistContext";
import { useTopics } from "@/hooks/useTopics";
import { useArticlesGroupedByTopic } from "@/hooks/useArticlesByTopic";
import { extractFaqFromContent } from "@/utils/extractFaqFromContent";
import { extractReferencesFromContent } from "@/utils/extractReferencesFromContent";
import { useArticleTracking } from "@/hooks/useArticleTracking";
import { useInView } from "react-intersection-observer";
import { trackEvent, ANALYTICS_EVENTS } from "@/lib/analytics";
import { buildUrl, DEFAULT_OG_IMAGE, SITE_URL } from "@/config/site";

/** CMS-driven article disclaimer (falls back to hardcoded if CMS unavailable) */
function ArticleDisclaimer() {
  const { data: disclaimer } = useDisclaimer('article-disclaimer');
  const fallback = 'This article is for educational purposes only and does not constitute financial advice. Private market investments carry risks including potential loss of capital. Past performance does not guarantee future results. Always do your own research and consider consulting a qualified financial advisor before making investment decisions.';
  
  return (
    <div className="max-w-3xl mx-auto pb-16">
      <p className="text-xs text-coal leading-relaxed">
        {disclaimer?.body || fallback}
      </p>
    </div>
  );
}

// Extend window for prerenderReady flag
declare global {
  interface Window {
    prerenderReady?: boolean;
  }
}

interface AuthorProfile {
  id: string;
  name: string;
  title: string;
  bio: string;
  byline: string | null;
  image_url: string | null;
  linkedin_url: string | null;
  years_experience: number | null;
}

interface BlogPost {
  id: string;
  title: string;
  subtitle: string | null;
  slug: string;
  excerpt: string | null;
  content: any;
  cover_image: string | null;
  status: string;
  published_at: string | null;
  created_at: string;
  updated_at: string;
  topic_id: string | null;
  author_profile_id: string | null;
  topics?: { name: string; slug: string; tag_color: string | null } | null;
  author_profile?: AuthorProfile | null;
  reviewer_profile_id?: string | null;
  reviewer_profile?: {
    id: string;
    name: string;
    title: string;
    bio: string;
    image_url: string | null;
    linkedin_url: string | null;
  } | null;
  related_1?: RelatedArticle | null;
  related_2?: RelatedArticle | null;
  related_3?: RelatedArticle | null;
}

function usePostBySlug(slug: string | undefined) {
  return useQuery({
    queryKey: ["post", slug],
    queryFn: async () => {
      if (!slug) return null;

      // First, fetch the main post
      const { data: post, error: postError } = await supabase
        .from("posts")
        .select(`
          *, 
          topics(name, slug, tag_color),
          author_profile:people_profiles!author_profile_id(
            id, name, title, bio, byline, image_url, linkedin_url, years_experience
          ),
          reviewer_profile:people_profiles!reviewer_profile_id(
            id, name, title, bio, image_url, linkedin_url
          )
        `)
        .eq("slug", slug)
        .eq("status", "published")
        .maybeSingle();

      if (postError) throw postError;
      if (!post) return null;

      // Collect related article IDs
      const relatedIds = [
        post.related_article_1_id,
        post.related_article_2_id,
        post.related_article_3_id,
      ].filter(Boolean) as string[];

      // Fetch related articles if any exist
      let relatedArticles: Record<string, RelatedArticle> = {};
      if (relatedIds.length > 0) {
        const { data: related, error: relatedError } = await supabase
          .from("posts")
          .select("id, title, slug, cover_image, topics(name, tag_color), author_profile:people_profiles!author_profile_id(name)")
          .in("id", relatedIds);

        if (relatedError) {
          console.error("Error fetching related articles:", relatedError);
        } else if (related) {
          // Map by ID for easy lookup
          relatedArticles = related.reduce((acc, article) => {
            acc[article.id] = article as RelatedArticle;
            return acc;
          }, {} as Record<string, RelatedArticle>);
        }
      }

      // Return post with related articles attached
      return {
        ...post,
        related_1: post.related_article_1_id ? relatedArticles[post.related_article_1_id] || null : null,
        related_2: post.related_article_2_id ? relatedArticles[post.related_article_2_id] || null : null,
        related_3: post.related_article_3_id ? relatedArticles[post.related_article_3_id] || null : null,
      } as BlogPost;
    },
    enabled: !!slug,
  });
}

// Fetch author experiences separately (only when needed for dialog)
function useAuthorExperiences(authorId: string | undefined) {
  return useQuery({
    queryKey: ["author-experiences", authorId],
    queryFn: async () => {
      if (!authorId) return [];
      
      const { data, error } = await supabase
        .from("people_profile_experiences")
        .select("id, experience")
        .eq("profile_id", authorId)
        .order("display_order", { ascending: true });

      if (error) throw error;
      return data || [];
    },
    enabled: !!authorId,
  });
}

// Fetch author credentials separately
function useAuthorCredentials(authorId: string | undefined) {
  return useQuery({
    queryKey: ["author-credentials", authorId],
    queryFn: async () => {
      if (!authorId) return [];
      const { data, error } = await supabase
        .from("people_profile_credentials")
        .select("id, credential")
        .eq("profile_id", authorId)
        .order("display_order", { ascending: true });
      if (error) throw error;
      return data || [];
    },
    enabled: !!authorId,
  });
}

// Fetch author education separately
function useAuthorEducation(authorId: string | undefined) {
  return useQuery({
    queryKey: ["author-education", authorId],
    queryFn: async () => {
      if (!authorId) return [];
      const { data, error } = await supabase
        .from("people_profile_education")
        .select("id, education")
        .eq("profile_id", authorId)
        .order("display_order", { ascending: true });
      if (error) throw error;
      return data || [];
    },
    enabled: !!authorId,
  });
}

// Fetch author specialisations separately
function useAuthorSpecialisations(authorId: string | undefined) {
  return useQuery({
    queryKey: ["author-specialisations", authorId],
    queryFn: async () => {
      if (!authorId) return [];
      const { data, error } = await supabase
        .from("people_profile_specialisations")
        .select("id, specialisation")
        .eq("profile_id", authorId)
        .order("display_order", { ascending: true });
      if (error) throw error;
      return data || [];
    },
    enabled: !!authorId,
  });
}

// Fetch reviewer credentials separately
function useReviewerCredentials(reviewerId: string | undefined) {
  return useQuery({
    queryKey: ["reviewer-credentials", reviewerId],
    queryFn: async () => {
      if (!reviewerId) return [];
      const { data, error } = await supabase
        .from("people_profile_credentials")
        .select("id, credential")
        .eq("profile_id", reviewerId)
        .order("display_order", { ascending: true });
      if (error) throw error;
      return data || [];
    },
    enabled: !!reviewerId,
  });
}

// Fetch reviewer education separately
function useReviewerEducation(reviewerId: string | undefined) {
  return useQuery({
    queryKey: ["reviewer-education", reviewerId],
    queryFn: async () => {
      if (!reviewerId) return [];
      const { data, error } = await supabase
        .from("people_profile_education")
        .select("id, education")
        .eq("profile_id", reviewerId)
        .order("display_order", { ascending: true });
      if (error) throw error;
      return data || [];
    },
    enabled: !!reviewerId,
  });
}

// Fetch reviewer specialisations separately
function useReviewerSpecialisations(reviewerId: string | undefined) {
  return useQuery({
    queryKey: ["reviewer-specialisations", reviewerId],
    queryFn: async () => {
      if (!reviewerId) return [];
      const { data, error } = await supabase
        .from("people_profile_specialisations")
        .select("id, specialisation")
        .eq("profile_id", reviewerId)
        .order("display_order", { ascending: true });
      if (error) throw error;
      return data || [];
    },
    enabled: !!reviewerId,
  });
}

/**
 * Build a rich Person schema for JSON-LD with full E-E-A-T signals.
 * Used for both author and reviewer profiles.
 */
function buildPersonSchema(profile: any) {
  if (!profile) return undefined;

  const person: Record<string, any> = {
    "@type": "Person",
    "@id": `${SITE_URL}/about#person-${profile.name.toLowerCase().replace(/\s+/g, '-')}`,
    name: profile.name,
    jobTitle: profile.title,
    description: profile.byline || profile.bio,
    url: `${SITE_URL}/about`,
    worksFor: { "@type": "Organization", "@id": `${SITE_URL}/#organization` },
  };

  if (profile.image_url) {
    person.image = profile.image_url;
  }

  if (profile.linkedin_url) {
    person.sameAs = [profile.linkedin_url];
  }

  if (profile.specialisations?.length > 0) {
    person.knowsAbout = profile.specialisations.map((s: any) =>
      typeof s === 'string' ? s : s.specialisation
    );
  }

  if (profile.credentials?.length > 0) {
    person.hasCredential = profile.credentials.map((c: any) => ({
      "@type": "EducationalOccupationalCredential",
      credentialCategory: "Professional Certification",
      name: typeof c === 'string' ? c : c.credential,
    }));
  }

  if (profile.education?.length > 0) {
    person.alumniOf = profile.education.map((e: any) => {
      const edu = typeof e === 'string' ? e : e.education;
      const parts = edu.split(' | ');
      if (parts.length >= 2) {
        return {
          "@type": "EducationalOrganization",
          name: parts[1].trim(),
          description: parts[0].trim(),
        };
      }
      return {
        "@type": "EducationalOrganization",
        name: edu,
      };
    });
  }

  return person;
}

// Estimate reading time from content
function estimateReadingTime(content: any): string {
  if (!content) return "1 min read";
  const text = JSON.stringify(content);
  const wordCount = text.split(/\s+/).length;
  const minutes = Math.max(1, Math.ceil(wordCount / 200));
  return `${minutes} min read`;
}

// Extract plain text from TipTap JSON node tree
function extractPlainText(node: any): string {
  if (!node) return '';
  if (typeof node === 'string') {
    try { node = JSON.parse(node); } catch { return ''; }
  }
  if (node.text) return node.text;
  if (node.content && Array.isArray(node.content)) {
    return node.content.map(extractPlainText).join(' ');
  }
  return '';
}

// Count words from TipTap content
function getWordCount(content: any): number {
  const text = extractPlainText(content).trim();
  if (!text) return 0;
  return text.split(/\s+/).length;
}

const NavButtons = ({ visible, onWaitlistClick }: { visible?: boolean; onWaitlistClick: (triggerElement?: string) => void }) => (
  <div className="flex items-center gap-8">
    <NavbarButton href="/contact" variant="secondary" visible={visible}>Contact</NavbarButton>
    <NavbarButton variant="primary" visible={visible} onClick={() => onWaitlistClick('navbar_cta')}>Join Waitlist</NavbarButton>
  </div>
);

/**
 * ⚠️ ANALYTICS FIX 4: Article Not Found component with ERROR_PAGE_VIEWED tracking
 * Fires a tracking event when a user visits an invalid blog slug
 */
function ArticleNotFound({
  slug,
  navItems,
  openModal,
}: {
  slug: string | undefined;
  navItems: { name: string; link: string }[];
  openModal: () => void;
}) {
  // Signal to Prerender.io that the page is ready (even for 404)
  useEffect(() => {
    if (typeof window !== 'undefined') {
      window.prerenderReady = true;
    }
  }, []);

  // ⚠️ ANALYTICS: Track article 404 view
  useEffect(() => {
    trackEvent(ANALYTICS_EVENTS.ERROR_PAGE_VIEWED, {
      error_type: 'article_not_found',
      attempted_slug: slug || 'unknown',
      attempted_path: window.location.pathname,
      referrer: document.referrer,
    });
  }, [slug]);

  return (
    <div className="min-h-screen bg-background">
      <Helmet>
        <title>Article Not Found | Fedgroup Private Markets</title>
        <meta name="robots" content="noindex, nofollow" />
        <meta name="prerender-status-code" content="404" />
      </Helmet>

      <Navbar forceVisible>
        <NavBody>
          <NavbarLogo logoWhite={logoWhite} logoBlack={logoBlack} />
          <NavItems items={navItems} />
          <NavButtons onWaitlistClick={openModal} />
        </NavBody>
      </Navbar>

      <div className="h-[44px]" />

      <div className="flex flex-col items-center justify-center py-32 px-4">
        <h1 className="text-4xl font-bold mb-4">Article Not Found</h1>
        <p className="text-muted-foreground mb-8 text-center max-w-md">
          The article you're looking for doesn't exist or has been removed.
        </p>
        <Button asChild>
          <Link to="/learn">
            <ArrowLeft className="h-4 w-4 mr-2" />
            Back to Learn
          </Link>
        </Button>
      </div>

      <Footer />
    </div>
  );
}

export default function Blog() {
  const { slug } = useParams<{ slug: string }>();
  const navigate = useNavigate();
  const { data: post, isLoading, error } = usePostBySlug(slug);
  const { data: authorExperiences } = useAuthorExperiences(post?.author_profile?.id);
  const { data: authorCredentials } = useAuthorCredentials(post?.author_profile?.id);
  const { data: authorEducation } = useAuthorEducation(post?.author_profile?.id);
  const { data: authorSpecialisations } = useAuthorSpecialisations(post?.author_profile?.id);
  const { data: reviewerCredentials } = useReviewerCredentials(post?.reviewer_profile?.id);
  const { data: reviewerEducation } = useReviewerEducation(post?.reviewer_profile?.id);
  const { data: reviewerSpecialisations } = useReviewerSpecialisations(post?.reviewer_profile?.id);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [authorDialogOpen, setAuthorDialogOpen] = useState(false);
  const { openModal } = useWaitlistModal();
  const { data: topics } = useTopics();
  const { data: articlesGrouped } = useArticlesGroupedByTopic();

  const navItems = [
    { name: "Invest", link: "/invest" },
    { name: "Learn", link: "/learn" },
    { name: "About", link: "/about" },
  ];

  // Build secondary nav items for topics (navigates to Learn page with topic filter)
  const topicSecondaryNavItems = useMemo(() => {
    const topicsWithArticles = (topics?.filter(t => t.is_active) || []).filter(topic => {
      const articles = articlesGrouped?.[topic.id];
      return articles && articles.length > 0;
    });
    
    return [
      { label: "All Articles", anchor: "all", onClick: () => navigate("/learn") },
      ...topicsWithArticles.map(t => ({
        label: t.name,
        anchor: t.slug,
        onClick: () => navigate(`/learn?topic=${t.slug}`),
      })),
    ];
  }, [topics, articlesGrouped, navigate]);

  // Signal to Prerender.io when article is loaded
  useEffect(() => {
    if (!isLoading && post && typeof window !== 'undefined') {
      window.prerenderReady = true;
    }
  }, [isLoading, post]);

  // Calculate read time (memoized to avoid recalculation)
  const readTime = useMemo(() => {
    if (!post?.content) return '5 min read';
    return estimateReadingTime(post.content);
  }, [post?.content]);

  // ⚠️ ANALYTICS: Ref for element-based article completion detection
  const articleEndRef = useRef<HTMLDivElement>(null);

  // ⚠️ ANALYTICS: Article footer section visibility tracking
  const { ref: articleFooterRef, inView: articleFooterInView } = useInView({
    threshold: 0.3,
    triggerOnce: true,
  });
  const hasTrackedFooter = useRef(false);

  useEffect(() => {
    if (articleFooterInView && !hasTrackedFooter.current && post) {
      hasTrackedFooter.current = true;
      trackEvent(ANALYTICS_EVENTS.SECTION_VIEWED, {
        section_id: 'article-footer',
        section_name: 'Article Footer',
        section_page: 'Article',
        section_position: 85,
      });
    }
  }, [articleFooterInView]);

  // ⚠️ ANALYTICS: Article tracking - hook called unconditionally, but only tracks when post exists
  const articleTracking = useArticleTracking({
    articleId: post?.id || '',
    articleSlug: post?.slug || '',
    articleTitle: post?.title || '',
    topicId: post?.topic_id || undefined,
    topicName: post?.topics?.name || 'Uncategorized',
    authorId: post?.author_profile?.id || undefined,
    authorName: post?.author_profile?.name || 'Fedgroup',
    estimatedReadTimeMinutes: parseInt(readTime) || 5,
    articleEndRef,
  });

  // ⚠️ ANALYTICS: Handle TOC click tracking
  const handleTocClick = useCallback((itemText: string, position: number) => {
    articleTracking.trackTocClick(itemText, position);
  }, [articleTracking]);

  // ⚠️ ANALYTICS: Handle related article click tracking
  const handleRelatedArticleClick = useCallback((
    clickedArticle: RelatedArticle,
    position: number
  ) => {
    articleTracking.trackRelatedClick(
      clickedArticle.id,
      clickedArticle.slug,
      clickedArticle.title,
      clickedArticle.topics?.name || '',
      position
    );
  }, [articleTracking]);

  // Loading state
  if (isLoading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  // 404 state — delegate to ArticleNotFound component with tracking
  if (!post || error) {
    return (
      <ArticleNotFound
        slug={slug}
        navItems={navItems}
        openModal={openModal}
      />
    );
  }

  const canonicalUrl = buildUrl(`/blog/${post.slug}`);
  
  // Build full author profile with all E-E-A-T data for bio card and dialog
  const authorProfileFull = post.author_profile ? {
    ...post.author_profile,
    experiences: authorExperiences || [],
    credentials: authorCredentials || [],
    education: authorEducation || [],
    specialisations: authorSpecialisations || [],
  } : undefined;

  // Build full reviewer profile with E-E-A-T data for JSON-LD
  const reviewerProfileFull = post.reviewer_profile ? {
    ...post.reviewer_profile,
    credentials: reviewerCredentials || [],
    education: reviewerEducation || [],
    specialisations: reviewerSpecialisations || [],
  } : undefined;

  return (
    <div className="min-h-screen bg-background">
      {/* SEO Meta Tags */}
      <Helmet>
        <title>{post.title} | Fedgroup Private Markets</title>
        <meta name="description" content={post.excerpt || post.subtitle || ""} />
        <link rel="canonical" href={canonicalUrl} />

        {/* Open Graph */}
        <meta property="og:type" content="article" />
        <meta property="og:title" content={post.title} />
        <meta property="og:description" content={post.excerpt || post.subtitle || ""} />
        <meta property="og:image" content={post.cover_image || DEFAULT_OG_IMAGE} />
        <meta property="og:url" content={canonicalUrl} />
        <meta property="og:site_name" content="Fedgroup" />
        {post.published_at && (
          <meta property="article:published_time" content={post.published_at} />
        )}
        {post.updated_at && (
          <meta property="article:modified_time" content={post.updated_at} />
        )}
        {post.author_profile && (
          <meta property="article:author" content={post.author_profile.name} />
        )}

        {/* Twitter Card */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={post.title} />
        <meta name="twitter:description" content={post.excerpt || post.subtitle || ""} />
        <meta name="twitter:image" content={post.cover_image || DEFAULT_OG_IMAGE} />

        {/* JSON-LD Structured Data */}
        <script type="application/ld+json">
          {(() => {
            const contentReferences = extractReferencesFromContent(post.content);
            const buildRelatedCitation = (relatedPost: RelatedArticle | null | undefined) => {
              if (!relatedPost) return null;
              const citation: Record<string, any> = {
                "@type": "Article",
                "headline": relatedPost.title,
                "url": buildUrl(`/blog/${relatedPost.slug}`),
              };
              if (relatedPost.cover_image) {
                citation.image = [relatedPost.cover_image];
              }
              if (relatedPost.author_profile?.name) {
                citation.author = { "@type": "Person", "name": relatedPost.author_profile.name };
              }
              return citation;
            };
            const relatedCitations = [post.related_1, post.related_2, post.related_3]
              .map(buildRelatedCitation)
              .filter(Boolean);
            const refCitations = contentReferences.map(r => ({
              "@type": "CreativeWork" as const,
              "name": r.name,
              "url": r.url,
            }));
            const allCitations = [...relatedCitations, ...refCitations];
            const wordCount = getWordCount(post.content);
            const articleBody = extractPlainText(post.content).substring(0, 5000);

            return JSON.stringify({
              "@context": "https://schema.org",
              "@type": "Article",
              "@id": canonicalUrl,
              headline: post.title,
              ...(post.subtitle ? { alternativeHeadline: post.subtitle } : {}),
              description: post.excerpt || post.subtitle,
              image: [post.cover_image],
              datePublished: post.published_at || post.created_at,
              dateModified: post.updated_at,
              author: authorProfileFull
                ? buildPersonSchema(authorProfileFull)
                : { "@type": "Organization", name: "Fedgroup" },
              ...(reviewerProfileFull ? {
                editor: buildPersonSchema(reviewerProfileFull),
              } : {}),
              publisher: {
                "@type": "Organization",
                "@id": `${SITE_URL}/#organization`,
                name: "Fedgroup Private Markets",
                logo: {
                  "@type": "ImageObject",
                  url: `${SITE_URL}/favicon-512x512.png`,
                },
              },
              mainEntityOfPage: {
                "@type": "WebPage",
                "@id": canonicalUrl,
              },
              inLanguage: "en",
              ...(wordCount > 0 ? { wordCount } : {}),
              ...(articleBody ? { articleBody } : {}),
              ...(post.topics?.name ? {
                articleSection: post.topics.name,
                about: { "@type": "Thing", name: post.topics.name },
              } : {}),
              ...(allCitations.length > 0 ? { citation: allCitations } : {}),
            });
          })()}
        </script>

        {/* BreadcrumbList Structured Data */}
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "BreadcrumbList",
            "itemListElement": [
              {
                "@type": "ListItem",
                "position": 1,
                "name": "Home",
                "item": buildUrl('/')
              },
              {
                "@type": "ListItem",
                "position": 2,
                "name": "Blog",
                "item": buildUrl('/learn')
              },
              {
                "@type": "ListItem",
                "position": 3,
                "name": post.title,
                "item": canonicalUrl
              }
            ]
          })}
        </script>

        {/* FAQPage Structured Data (only if article has FAQs) */}
        {(() => {
          const faqs = extractFaqFromContent(post.content);
          if (faqs.length === 0) return null;
          return (
            <script type="application/ld+json">
              {JSON.stringify({
                "@context": "https://schema.org",
                "@type": "FAQPage",
                "mainEntity": faqs.map(faq => ({
                  "@type": "Question",
                  "name": faq.question,
                  "acceptedAnswer": {
                    "@type": "Answer",
                    "text": faq.answer
                  }
                }))
              })}
            </script>
          );
        })()}
      </Helmet>

      {/* Navigation */}
      <Navbar forceVisible>
        <NavBody>
          <NavbarLogo logoWhite={logoWhite} logoBlack={logoBlack} />
          <NavItems items={navItems} />
          <NavButtons onWaitlistClick={openModal} />
        </NavBody>

        <MobileNav>
          <MobileNavHeader>
            <NavbarLogo logoWhite={logoWhite} logoBlack={logoBlack} />
            <MobileNavToggle
              isOpen={isMobileMenuOpen}
              onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
            />
          </MobileNavHeader>

          <MobileNavMenu
            isOpen={isMobileMenuOpen}
            onClose={() => setIsMobileMenuOpen(false)}
          >
            <MobileMenuContent
              navItems={navItems}
              onItemClick={() => setIsMobileMenuOpen(false)}
              onWaitlistClick={openModal}
              secondaryNavItems={topicSecondaryNavItems}
            />
          </MobileNavMenu>
        </MobileNav>
      </Navbar>

      {/* Spacer for fixed navbar */}
      <div className="h-[44px]" />

      {/* Table of Contents - fixed overlay on left for wide screens */}
      <ArticleTableOfContents content={post.content} onTocClick={handleTocClick} />

      {/* Floating action buttons - bottom right, scroll-reactive */}
      <FloatingArticleNav />

      {/* Article */}
      <article className="px-4 overflow-x-clip">
        <div className="max-w-4xl mx-auto">
          {/* Header using design system component */}
          <div className="max-w-3xl mx-auto pt-12 pb-8">
            <ArticleHeader
              title={post.title}
              subtitle={post.subtitle || undefined}
              topicName={post.topics?.name}
              topicColor={post.topics?.tag_color || undefined}
              publishedAt={post.published_at || post.created_at}
              readTime={readTime}
              authorProfile={authorProfileFull}
              backLink={{ href: "/learn", label: "Back to Learn" }}
              shareUrl={canonicalUrl}
            />
          </div>

          {/* Content with prose-article class for DS lineage */}
          <div className="max-w-3xl mx-auto overflow-visible prose-article">
            <PostViewer content={post.content} />
          </div>

          {/* ⚠️ ANALYTICS: Zero-height marker for element-based article completion */}
          <div ref={articleEndRef} className="h-0 w-full" aria-hidden="true" />

          {/* Author Bio Card + Share */}
            <div ref={articleFooterRef} className="max-w-3xl mx-auto pb-8 pt-6 mt-6 border-t border-border">
              <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-8">
                {/* Author card */}
                {authorProfileFull && (
                  <div className="flex-1 min-w-0 sm:pr-8">
                    <p className="text-sm text-muted-foreground mb-4">Written by</p>
                    <button
                      type="button"
                      className="w-full text-left group"
                      onClick={() => {
                        setAuthorDialogOpen(true);
                        // ⚠️ ANALYTICS: Track author bio card click
                        trackEvent(ANALYTICS_EVENTS.AUTHOR_CLICKED, {
                          author_id: authorProfileFull.id,
                          author_name: authorProfileFull.name,
                          click_location: 'article_footer_bio_card',
                          article_slug: post.slug,
                        });
                        trackEvent(ANALYTICS_EVENTS.MODAL_OPENED, {
                          modal_type: 'people_profile',
                          trigger_element: 'author_bio_card',
                          profile_id: authorProfileFull.id,
                          profile_name: authorProfileFull.name,
                        });
                      }}
                    >
                      <div className="flex gap-4">
                        {/* Avatar — 56px */}
                        <div className="shrink-0">
                          {authorProfileFull.image_url ? (
                            <img
                              src={optimizeImage(authorProfileFull.image_url, { width: 112 })}
                              alt={authorProfileFull.name}
                              decoding="async"
                              loading="lazy"
                              width={56}
                              height={56}
                              className="w-14 h-14 rounded-full object-cover object-top"
                            />
                          ) : (
                            <div className="w-14 h-14 rounded-full bg-muted flex items-center justify-center">
                              <User className="w-6 h-6 text-muted-foreground" />
                            </div>
                          )}
                        </div>

                        {/* Content */}
                        <div className="flex-1 min-w-0">
                          <h3 className="text-[19px] font-semibold text-carbon leading-tight">{authorProfileFull.name}</h3>
                          <p className="text-[14px] text-coal mt-0.5">{authorProfileFull.title}</p>

                          <p className="text-[14px] text-carbon leading-relaxed mt-2 line-clamp-2">
                            {authorProfileFull.byline || (authorProfileFull.bio.length > 160 ? authorProfileFull.bio.slice(0, 160) + '…' : authorProfileFull.bio)}
                          </p>

                          <span className="inline-flex items-center gap-1 text-[13px] font-medium text-coal group-hover:text-carbon transition-colors mt-2">
                            View full profile
                            <ArrowRight className="w-3.5 h-3.5" />
                          </span>
                        </div>
                      </div>
                    </button>

                    {/* People Profile Dialog */}
                    <PeopleProfileDialog
                      profile={authorProfileFull}
                      open={authorDialogOpen}
                      onOpenChange={setAuthorDialogOpen}
                    />
                  </div>
                )}

                {/* Share bar */}
                <div className="shrink-0">
                  <p className="text-sm text-muted-foreground mb-4">Share</p>
                  <ShareBar url={canonicalUrl} title={post.title} variant="compact" />
                </div>
              </div>
            </div>

            {/* Article freshness - only shown if published_at exists */}
            {post.published_at && (
              <div className="max-w-3xl mx-auto pb-6">
                <ArticleFreshness
                  publishedAt={post.published_at}
                  updatedAt={post.updated_at}
                />
              </div>
            )}

            {/* Standard disclaimer - CMS managed */}
            <ArticleDisclaimer />
          </div>
        </article>

      {/* Related Articles Section - only shown if all 3 related articles exist */}
      {post.related_1 && post.related_2 && post.related_3 && (
        <RelatedArticlesSection
          articles={[post.related_1, post.related_2, post.related_3]}
          onArticleClick={handleRelatedArticleClick}
        />
      )}

      {/* Dynamic CTA Footer - assigned per article via CMS */}
      {/* Force white background detection since article pages use bg-background (white) */}
      <DynamicCtaFooter
        pageSlug={`/blog/${slug}`}
        precedingSectionColor={post.related_1 && post.related_2 && post.related_3 ? "rgb(245, 245, 247)" : "rgb(255, 255, 255)"}
      />

      <Footer />
    </div>
  );
}
